import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { splashServerMessage } from "@/lib/splash/serverMessage";
import type {
  AppSplashAppResponse,
  AppSplashRequest,
  AppSplashResponse,
} from "@/types/generated/cms.generated";

/** The splash list (`GET /app-splashes`). */
export const APP_SPLASHES_KEY = ["app-splashes"] as const;
const APP_SPLASH_APPS_KEY = ["app-splashes", "apps"] as const;

/** Every mutation refreshes the list only (the app list never changes from this page). */
function useInvalidateSplashes() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: APP_SPLASHES_KEY, exact: true });
}

/** Every occasion splash (not deleted), newest start first. */
export function useAppSplashes() {
  return useQuery<AppSplashResponse[]>({
    queryKey: APP_SPLASHES_KEY,
    queryFn: () => apiRequest("GET", "/app-splashes"),
  });
}

/** The apps a splash may be scheduled for (SP-D21) — the App select and the table filter. */
export function useAppSplashApps() {
  return useQuery<AppSplashAppResponse[]>({
    queryKey: APP_SPLASH_APPS_KEY,
    queryFn: () => apiRequest("GET", "/app-splashes/apps"),
  });
}

export function useCreateAppSplash() {
  const invalidate = useInvalidateSplashes();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: AppSplashRequest) => apiRequest<AppSplashResponse>("POST", "/app-splashes", data),
    onSuccess: () => {
      void invalidate();
      toast({ title: "Splash added", description: "It's in the list with its status." });
    },
    onError: (error: unknown) => {
      toast({
        title: "Couldn't save the splash",
        description: splashServerMessage(error, "Failed to save the splash. Please try again."),
        variant: "destructive",
      });
    },
  });
}

export function useUpdateAppSplash() {
  const invalidate = useInvalidateSplashes();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AppSplashRequest }) =>
      apiRequest<AppSplashResponse>("PUT", `/app-splashes/${id}`, data),
    onSuccess: () => {
      void invalidate();
      toast({ title: "Splash saved", description: "Phones get the change from their next-but-one open." });
    },
    onError: (error: unknown) => {
      toast({
        title: "Couldn't save the splash",
        description: splashServerMessage(error, "Failed to save the splash. Please try again."),
        variant: "destructive",
      });
    },
  });
}

/** The row's on/off switch. A refused switch-on (e.g. an overlap) toasts the server's own sentence. */
export function useSetAppSplashActive() {
  const invalidate = useInvalidateSplashes();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiRequest<AppSplashResponse>("PUT", `/app-splashes/${id}/active`, { isActive }),
    onSuccess: (_data, { isActive }) => {
      void invalidate();
      toast({ title: isActive ? "Splash switched on" : "Splash switched off" });
    },
    onError: (error: unknown, { isActive }) => {
      toast({
        title: isActive ? "Couldn't switch it on" : "Couldn't switch it off",
        description: splashServerMessage(error, "Please try again."),
        variant: "destructive",
      });
    },
  });
}

export function useDeleteAppSplash() {
  const invalidate = useInvalidateSplashes();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/app-splashes/${id}`),
    onSuccess: () => {
      void invalidate();
      toast({ title: "Splash deleted", description: "Phones stop showing it from their next-but-one open." });
    },
    onError: (error: unknown) => {
      toast({
        title: "Couldn't delete the splash",
        description: splashServerMessage(error, "Failed to delete the splash. Please try again."),
        variant: "destructive",
      });
    },
  });
}
